import React, { useMemo, useState, Fragment } from 'react';
import { SearchIcon, FilterIcon, CheckIcon, AlertCircleIcon, XIcon, ChevronDownIcon, ChevronUpIcon, AlertTriangleIcon, RefreshCwIcon } from 'lucide-react';
import { Lead, RoutingStatus, ErrorCode } from '../types/leads';
interface UnresolvedLeadsTableProps {
  leads: Lead[];
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onSelectAll: () => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  sourceFilter: string | null;
  onSourceFilterChange: (source: string | null) => void;
  statusFilter: RoutingStatus | null;
  onStatusFilterChange: (status: RoutingStatus | null) => void;
  onPushSelected: () => void;
  onPushSingle: (id: string) => void;
  availableSources: string[];
}
type SortField = 'name' | 'leadSource' | 'createdAt' | 'status' | 'errorCode';
type SortDirection = 'asc' | 'desc';
const ERROR_TYPE_LABELS: Record<ErrorCode, string> = {
  SF_API_TIMEOUT: 'API Timeout',
  INVALID_PHONE: 'Invalid Phone',
  DUPLICATE_LEAD: 'Duplicate Lead',
  LIST_NOT_FOUND: 'List Not Found',
  AUTH_EXPIRED: 'Auth Expired',
  FIELD_MAPPING: 'Field Mapping',
  RATE_LIMITED: 'Rate Limited'
};
export function UnresolvedLeadsTable({
  leads,
  selectedIds,
  onToggleSelect,
  onSelectAll,
  searchQuery,
  onSearchChange,
  sourceFilter,
  onSourceFilterChange,
  statusFilter,
  onStatusFilterChange,
  onPushSelected,
  onPushSingle,
  availableSources
}: UnresolvedLeadsTableProps) {
  const [errorFilter, setErrorFilter] = useState<ErrorCode | null>(null);
  const [expandedErrorId, setExpandedErrorId] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  const sortedAndFilteredLeads = useMemo(() => {
    // First filter
    const filtered = leads.filter(lead => {
      if (errorFilter && lead.errorCode !== errorFilter) return false;
      return true;
    });
    // Then sort
    return filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'leadSource':
          comparison = a.leadSource.localeCompare(b.leadSource);
          break;
        case 'createdAt':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
        case 'errorCode':
          const codeA = a.errorCode || '';
          const codeB = b.errorCode || '';
          comparison = codeA.localeCompare(codeB);
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [leads, errorFilter, sortField, sortDirection]);
  const allSelected = sortedAndFilteredLeads.length > 0 && selectedIds.size === sortedAndFilteredLeads.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < sortedAndFilteredLeads.length;
  const getStatusBadge = (lead: Lead) => {
    switch (lead.status) {
      case 'missing':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-270">
            Missing
          </span>;
      case 'error':
        const isExpanded = expandedErrorId === lead.id;
        return <button onClick={e => {
          e.stopPropagation();
          setExpandedErrorId(isExpanded ? null : lead.id);
        }} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1" aria-expanded={isExpanded} data-id="element-271">
            Error
            <ChevronDownIcon className={`ml-1 h-3 w-3 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} data-id="element-272" />
          </button>;
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-273">
            Pending
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-274">
            {lead.status}
          </span>;
    }
  };
  const formatTime = (isoString: string) => {
    return new Date(isoString).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const SortIcon = ({
    field
  }: {
    field: SortField;
  }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUpIcon className="ml-1 h-3 w-3 inline" data-id="element-275" /> : <ChevronDownIcon className="ml-1 h-3 w-3 inline" data-id="element-276" />;
  };
  const SortableHeader = ({
    field,
    label,
    className = ''
  }: {
    field: SortField;
    label: string;
    className?: string;
  }) => <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors ${sortField === field ? 'text-gray-900 font-semibold' : 'text-gray-500'} ${className}`} onClick={() => handleSort(field)} data-id="element-277">
      <div className="flex items-center" data-id="element-278">
        {label}
        <SortIcon field={field} data-id="element-279" />
      </div>
    </th>;
  return <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col" data-id="element-280">
      {/* Header & Toolbar */}
      <div className="p-5 border-b border-gray-200 bg-white" data-id="element-281">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4" data-id="element-282">
          <div className="flex items-center" data-id="element-283">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-284">
              Leads Missing from Dialing Lists
            </h2>
            <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-285">
              {sortedAndFilteredLeads.length} Unresolved
            </span>
          </div>

          <button onClick={onPushSelected} disabled={selectedIds.size === 0} className={`
              inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors
              ${selectedIds.size > 0 ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'}
            `} data-id="element-286">
            Push {selectedIds.size > 0 ? selectedIds.size : ''} Selected to
            Lists
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3" data-id="element-287">
          {/* Search */}
          <div className="relative flex-1" data-id="element-288">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-289">
              <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-290" />
            </div>
            <input type="text" placeholder="Search by name, email, or phone..." value={searchQuery} onChange={e => onSearchChange(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-291" />
          </div>

          {/* Filters */}
          <div className="flex gap-3 flex-wrap sm:flex-nowrap" data-id="element-292">
            <div className="relative w-full sm:w-auto" data-id="element-293">
              <select value={sourceFilter || ''} onChange={e => onSourceFilterChange(e.target.value || null)} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-lg border appearance-none bg-white" data-id="element-294">
                <option value="" data-id="element-295">All Sources</option>
                {availableSources.map(s => <option key={s} value={s} data-id="element-296">
                    {s}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500" data-id="element-297">
                <FilterIcon className="h-4 w-4" data-id="element-298" />
              </div>
            </div>

            <div className="relative w-full sm:w-auto" data-id="element-299">
              <select value={statusFilter || ''} onChange={e => {
              onStatusFilterChange(e.target.value as RoutingStatus || null);
              if (e.target.value !== 'error') setErrorFilter(null);
            }} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-lg border appearance-none bg-white" data-id="element-300">
                <option value="" data-id="element-301">All Statuses</option>
                <option value="missing" data-id="element-302">Missing</option>
                <option value="error" data-id="element-303">Error</option>
                <option value="pending" data-id="element-304">Pending</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500" data-id="element-305">
                <FilterIcon className="h-4 w-4" data-id="element-306" />
              </div>
            </div>

            <div className="relative w-full sm:w-auto" data-id="element-307">
              <select value={errorFilter || ''} onChange={e => {
              setErrorFilter(e.target.value as ErrorCode || null);
              if (e.target.value) onStatusFilterChange('error');
            }} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-lg border appearance-none bg-white" data-id="element-308">
                <option value="" data-id="element-309">All Error Types</option>
                {Object.entries(ERROR_TYPE_LABELS).map(([code, label]) => <option key={code} value={code} data-id="element-310">
                    {label}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500" data-id="element-311">
                <AlertTriangleIcon className="h-4 w-4" data-id="element-312" />
              </div>
            </div>
          </div>
        </div>

        {/* Active Filter Chips */}
        {(sourceFilter || statusFilter || errorFilter || searchQuery) && <div className="flex items-center gap-2 mt-4 flex-wrap" data-id="element-313">
            <span className="text-xs text-gray-500 font-medium" data-id="element-314">
              Active Filters:
            </span>
            {sourceFilter && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-315">
                Source: {sourceFilter}
                <button type="button" onClick={() => onSourceFilterChange(null)} className="ml-1.5 inline-flex text-blue-500 hover:text-blue-800" data-id="element-316">
                  <XIcon className="h-3 w-3" data-id="element-317" />
                </button>
              </span>}
            {statusFilter && !errorFilter && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-318">
                Status: {statusFilter}
                <button type="button" onClick={() => onStatusFilterChange(null)} className="ml-1.5 inline-flex text-blue-500 hover:text-blue-800" data-id="element-319">
                  <XIcon className="h-3 w-3" data-id="element-320" />
                </button>
              </span>}
            {errorFilter && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800" data-id="element-321">
                Error: {ERROR_TYPE_LABELS[errorFilter]}
                <button type="button" onClick={() => setErrorFilter(null)} className="ml-1.5 inline-flex text-amber-600 hover:text-amber-800" data-id="element-322">
                  <XIcon className="h-3 w-3" data-id="element-323" />
                </button>
              </span>}
            {searchQuery && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-324">
                Search: "{searchQuery}"
                <button type="button" onClick={() => onSearchChange('')} className="ml-1.5 inline-flex text-blue-500 hover:text-blue-800" data-id="element-325">
                  <XIcon className="h-3 w-3" data-id="element-326" />
                </button>
              </span>}
          </div>}
      </div>

      {/* Table */}
      <div className="overflow-x-auto" data-id="element-327">
        {sortedAndFilteredLeads.length > 0 ? <table className="min-w-full divide-y divide-gray-200" data-id="element-328">
            <thead className="bg-gray-50" data-id="element-329">
              <tr data-id="element-330">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12" data-id="element-331">
                  <div className="flex items-center" data-id="element-332">
                    <input type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer" checked={allSelected} ref={input => {
                  if (input) input.indeterminate = someSelected;
                }} onChange={onSelectAll} aria-label="Select all visible leads" data-id="element-333" />
                  </div>
                </th>
                <SortableHeader field="name" label="Lead Details" data-id="element-334" />
                <SortableHeader field="leadSource" label="Routing Info" data-id="element-335" />
                <SortableHeader field="createdAt" label="Created" data-id="element-336" />
                <SortableHeader field="status" label="Status" data-id="element-337" />
                <SortableHeader field="errorCode" label="Error Type" className="hidden lg:table-cell" data-id="element-338" />
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-339">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-340">
              {sortedAndFilteredLeads.map(lead => {
            const isSelected = selectedIds.has(lead.id);
            const isExpanded = expandedErrorId === lead.id && lead.status === 'error';
            return <Fragment key={lead.id} data-id="element-341">
                    <tr className={`hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50/50' : ''} ${isExpanded ? 'bg-amber-50/30' : ''}`} data-id="element-342">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-343">
                        <div className="flex items-center" data-id="element-344">
                          <input type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer" checked={isSelected} onChange={() => onToggleSelect(lead.id)} aria-label={`Select ${lead.name}`} data-id="element-345" />
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-346">
                        <div className="flex flex-col" data-id="element-347">
                          <span className="text-sm font-medium text-gray-900" data-id="element-348">
                            {lead.name}
                          </span>
                          <span className="text-sm text-gray-500" data-id="element-349">
                            {lead.email}
                          </span>
                          <span className="text-xs text-gray-400 mt-0.5" data-id="element-350">
                            {lead.phone}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-351">
                        <div className="flex flex-col" data-id="element-352">
                          <span className="text-sm text-gray-900 font-medium" data-id="element-353">
                            {lead.leadSource}
                          </span>
                          <div className="flex items-center text-xs text-gray-500 mt-1" data-id="element-354">
                            <span className="text-gray-400 mr-1" data-id="element-355">Target:</span>
                            <span className="truncate max-w-[150px]" data-id="element-356">
                              {lead.expectedDialingList}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-357">
                        {formatTime(lead.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-358">
                        {getStatusBadge(lead)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap hidden lg:table-cell" data-id="element-359">
                        {lead.errorCode ? <span className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-1 rounded" data-id="element-360">
                            {lead.errorCode}
                          </span> : <span className="text-gray-300" data-id="element-361">-</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-362">
                        <button onClick={() => onPushSingle(lead.id)} className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-363">
                          Push to List
                        </button>
                      </td>
                    </tr>

                    {/* Expanded Error Row */}
                    {isExpanded && <tr className="bg-amber-50 border-l-4 border-l-amber-400" data-id="element-364">
                        <td colSpan={7} className="px-6 py-4" data-id="element-365">
                          <div className="flex items-start justify-between" data-id="element-366">
                            <div className="flex items-start space-x-3" data-id="element-367">
                              <AlertTriangleIcon className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" data-id="element-368" />
                              <div data-id="element-369">
                                <div className="flex items-center space-x-2 mb-1" data-id="element-370">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded bg-amber-200 text-amber-900 text-xs font-mono font-medium" data-id="element-371">
                                    {lead.errorCode}
                                  </span>
                                  <span className="text-sm font-medium text-amber-900" data-id="element-372">
                                    Routing Failed
                                  </span>
                                </div>
                                <p className="text-sm text-amber-800 max-w-3xl" data-id="element-373">
                                  {lead.errorMessage || 'An unknown error occurred while routing this lead.'}
                                </p>
                              </div>
                            </div>
                            <button onClick={() => onPushSingle(lead.id)} className="inline-flex items-center px-3 py-1.5 border border-amber-300 shadow-sm text-xs font-medium rounded text-amber-700 bg-amber-100 hover:bg-amber-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500" data-id="element-374">
                              <RefreshCwIcon className="mr-1.5 h-3.5 w-3.5" data-id="element-375" />
                              Retry Push
                            </button>
                          </div>
                        </td>
                      </tr>}
                  </Fragment>;
          })}
            </tbody>
          </table> : <div className="text-center py-16 px-4" data-id="element-376">
            {sourceFilter || statusFilter || errorFilter || searchQuery ? <>
                <AlertCircleIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" data-id="element-377" />
                <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-378">
                  No leads match your filters
                </h3>
                <p className="text-gray-500 mb-4" data-id="element-379">
                  Try adjusting your search or filter criteria.
                </p>
                <button onClick={() => {
            onSourceFilterChange(null);
            onStatusFilterChange(null);
            setErrorFilter(null);
            onSearchChange('');
          }} className="text-blue-600 hover:text-blue-800 font-medium text-sm" data-id="element-380">
                  Clear all filters
                </button>
              </> : <>
                <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mb-4" data-id="element-381">
                  <CheckIcon className="h-8 w-8 text-green-600" data-id="element-382" />
                </div>
                <h3 className="text-xl font-medium text-gray-900 mb-2" data-id="element-383">
                  All Caught Up!
                </h3>
                <p className="text-gray-500 max-w-sm mx-auto" data-id="element-384">
                  There are no leads missing from their dialing lists right now.
                  Great work keeping the queues full.
                </p>
              </>}
          </div>}
      </div>

      {/* Footer */}
      {sortedAndFilteredLeads.length > 0 && <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 text-sm text-gray-500 flex justify-between items-center" data-id="element-385">
          <span data-id="element-386">Showing {sortedAndFilteredLeads.length} unresolved leads</span>
          {selectedIds.size > 0 && <span className="font-medium text-blue-600" data-id="element-387">
              {selectedIds.size} selected
            </span>}
        </div>}
    </div>;
}